import {
  INFO_ANSWER_IDS_KEY, MAX_DOCUMENT_FILENAME_LENGTH, INFO_ANSWER_MAX, INFO_ANSWER_TEXTS_KEY, cleanInfoAnswer, containsRedactedNumber, safeDocumentFilename,
  type AccountNumberState, type InfoRequestForClient,
} from '@steward-app/steward-types';
import { claimsADocumentType, documentTypeOfBytes, readFileHead, splitNameForType, type DocumentContentType } from '../../lib/documentSignature';
import type { Values } from './reviewAnswers';

/**
 * The pure half of the form's "Requests from your advisor" step (crossmarc/steward-app#2284):
 * which files may be added, how a typed answer is stored beside the others, and what the step
 * and the review say about each request. Nothing here fetches.
 *
 * A typed answer is stored as two index-aligned lists in the `requests` section, the request id
 * and the text (the same shape as the children's lists). The lists are paired BY INDEX and read
 * back by id. A request id that is not in the form's request list is not shown, and
 * {@link withAnswer} keeps its pair when it rewrites the lists (the section save replaces the whole
 * section, so dropping it would delete it), with two exceptions: when the lists would pass
 * {@link MAX_ANSWER_ITEMS} such pairs are dropped first, and a pair with a blank id, or a second
 * pair for an id already seen, is dropped on any change. The text of a kept pair is not altered
 * here (the save and the façade clean it).
 */

/** The most one file may be, in bytes: appcore's `ONBOARDING_CLIENT_DOCUMENT_MAX_BYTES` (25 MB),
 *  read at appcore origin/main 997f437 on 2026-10-10 and copied, not imported. The façade's mint
 *  body carries the same figure as a literal (`handlers/onboardingForm.ts`), and the words "25 MB"
 *  in the step's copy are typed by hand: a change here means changing those too. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
/** appcore's cap on one list in a section save (read at appcore origin/main, 2026-10-10). */
export const MAX_ANSWER_ITEMS = 50;

export type FileCheck =
  | { ok: true; filename: string; mimeType: DocumentContentType }
  | { ok: false; reason: 'type' | 'size' | 'empty' | 'mismatch' };

/**
 * What to send for a chosen file, decided by what the file IS (crossmarc/steward-app#2327): its
 * first bytes ({@link documentTypeOfBytes}), never `File.type` or the extension. `mimeType` is
 * the type to declare at the mint and on the PUT, and `filename` carries an extension that
 * matches it (appcore binds the extension to the declared type). So a PNG saved as `photo.jpg`
 * goes up as `photo.png`, an `image/png`.
 *
 * Refused before any request: an empty file or one over the cap; bytes that are none of the three
 * under a name that claims one of them (`mismatch`, the same sentence appcore's commit gives);
 * bytes that are none of the three under any other name (`type`). A file that cannot be read is a
 * `mismatch`. The three accepted types are appcore's `ONBOARDING_CLIENT_DOCUMENT_MIME_TYPES`
 * (read at appcore origin/main 6b02f97 on 2026-10-13); a kindness, not a control.
 */
export async function checkUploadFile(file: File): Promise<FileCheck> {
  if (file.size <= 0) return { ok: false, reason: 'empty' };
  if (file.size > MAX_UPLOAD_BYTES) return { ok: false, reason: 'size' };
  let mimeType: DocumentContentType | null;
  try {
    mimeType = documentTypeOfBytes(await readFileHead(file));
  } catch {
    return { ok: false, reason: 'mismatch' };
  }
  if (mimeType === null) return { ok: false, reason: claimsADocumentType(file.name) ? 'mismatch' : 'type' };
  const { base, extension } = splitNameForType(safeDocumentFilename(file.name), mimeType);
  // The whole name stays within appcore's cap (it slices to 200, which would cut the extension off
  // and fail its filename/type check). A plain cut of the base, with no ellipsis (appcore turns
  // that into `_`).
  const room = MAX_DOCUMENT_FILENAME_LENGTH - extension.length - 1;
  return { ok: true, filename: `${base.slice(0, room)}.${extension}`, mimeType };
}

/** Shown above requests the firm withdrew that hold a file or answer of the client's (steward-app#2302).
 *  "Still with them" rests on the staff read returning a withdrawn request's files and answer
 *  (`readClientProvided`, pinned in the façade's tests); it says nothing about how long. */
export const WITHDRAWN_HEADING = 'No longer needed';
export const WITHDRAWN_LINE_AFTER_FIRM = ' no longer needs these. What you added is still with them.';

/** What the request is called on screen and in the review. */
export function requestTitle(r: Pick<InfoRequestForClient, 'kind' | 'prompt' | 'accountLabel'>): string {
  if (r.kind === 'statement') return r.accountLabel ? `Statement for ${r.accountLabel}` : 'A recent statement';
  if (r.kind === 'account-number') return r.accountLabel ? `Account number for ${r.accountLabel}` : 'An account number';
  return r.prompt ?? (r.kind === 'document' ? 'A document' : 'A question');
}

function pairs(values: Values): Array<[string, string]> {
  const ids = values[INFO_ANSWER_IDS_KEY];
  const texts = values[INFO_ANSWER_TEXTS_KEY];
  if (!Array.isArray(ids)) return [];
  return ids.map((id, i): [string, string] => [typeof id === 'string' ? id : '', Array.isArray(texts) && typeof texts[i] === 'string' ? texts[i] as string : '']);
}

/** The text as it was typed (not cleaned), for the box. The first pair for the id wins. */
export function rawAnswer(values: Values, requestId: string): string {
  return pairs(values).find(([id]) => id === requestId)?.[1] ?? '';
}

/** The answer as it will be stored, for the review. */
export function storedAnswer(values: Values, requestId: string): string {
  const raw = rawAnswer(values, requestId);
  return raw.trim() === '' ? '' : cleanInfoAnswer(raw);
}

/**
 * The two lists with `requestId`'s answer set to `text`. A blank text removes that id's pair so
 * the lists stay aligned and a cleared box is cleared. Pairs for other ids are kept as they are
 * (including ids no longer in `knownIds`), unless the lists would pass {@link MAX_ANSWER_ITEMS}:
 * then pairs for ids NOT in `knownIds` are dropped first, and if that is not enough the change is
 * not made (`undefined`).
 */
export function withAnswer(values: Values, requestId: string, text: string, knownIds: readonly string[]): { ids: string[]; texts: string[] } | undefined {
  let next = pairs(values).filter(([id], i, all) => id !== '' && all.findIndex(([x]) => x === id) === i);
  const blank = text.trim() === '';
  const at = next.findIndex(([id]) => id === requestId);
  if (blank) next = next.filter(([id]) => id !== requestId);
  else if (at >= 0) next = next.map((p) => (p[0] === requestId ? [requestId, text] : p));
  else next = [...next, [requestId, text]];
  if (next.length > MAX_ANSWER_ITEMS) next = next.filter(([id]) => id === requestId || knownIds.includes(id));
  if (next.length > MAX_ANSWER_ITEMS) return undefined;
  return { ids: next.map(([id]) => id), texts: next.map(([, t]) => t) };
}

/** What changed when a typed answer was cleaned for storage, or null when nothing the client
 *  would notice. Compared with the same text with only its layout tidied. */
export function answerChange(raw: string): 'numbers' | 'long' | null {
  const tidy = raw.replace(/\s+/g, ' ').trim();
  if (tidy.length > INFO_ANSWER_MAX) return 'long';
  const cleaned = cleanInfoAnswer(raw);
  return containsRedactedNumber(cleaned) && !containsRedactedNumber(tidy) ? 'numbers' : null;
}

/** The requests whose number the server says is saved (`AccountNumberState.filled`). The ONLY
 *  evidence that answers an account-number request: nothing typed in a box counts, and nothing in
 *  the form's `values` is consulted (docs/38 §5). */
export function filledNumberIds(states: readonly AccountNumberState[] | undefined): ReadonlySet<string> {
  return new Set((states ?? []).filter((s) => s.filled).map((s) => s.requestId));
}

/** What a saved number reads as, from the server's hint and never from anything typed. A number
 *  too short to give a hint reads as just saved. */
export function savedLine(hint: string | undefined): string {
  return hint ? `Saved, ending in ${hint}` : 'Saved';
}

/** The review page's line for a request that has a saved number: the hint only. */
export function reviewNumberValue(state: AccountNumberState | undefined): string {
  if (!state?.filled) return '';
  if (state.accountHint) return `Ending in ${state.accountHint}`;
  // A number the firm entered is not "Saved" by the client (docs/38 §4.5).
  return state.enteredBy === 'firm' ? 'Your advisor has it' : 'Saved';
}

/** `list` with `state` in place of any state for the same request. */
export function withNumberState(list: readonly AccountNumberState[] | undefined, state: AccountNumberState): AccountNumberState[] {
  return [...(list ?? []).filter((s) => s.requestId !== state.requestId), state];
}

/** How many of the requests have something from the client: a file, a non-blank answer, or (for an
 *  account number) a number the server holds. */
export function answeredCount(
  requests: readonly InfoRequestForClient[], uploads: Record<string, readonly unknown[]>, values: Values, filled: ReadonlySet<string> = new Set(),
): number {
  return requests.filter((r) => (r.kind === 'question'
    ? storedAnswer(values, r.id) !== ''
    : r.kind === 'account-number' ? filled.has(r.id) : (uploads[r.id]?.length ?? 0) > 0)).length;
}

/**
 * Which requests have something from the client and which do not, in the form's own order, by
 * the rule {@link answeredCount} counts with. This is the only evidence the client's browser
 * holds: each request carries `requestedAt`, but nothing says when the client supplied an item,
 * so "added" means the file or answer is saved on the form, with no date of its own.
 */
export function splitRequests<T extends InfoRequestForClient>(
  requests: readonly T[], uploads: Record<string, readonly unknown[]>, values: Values, filled: ReadonlySet<string> = new Set(),
): { open: T[]; added: T[] } {
  const added = requests.filter((r) => answeredCount([r], uploads, values, filled) === 1);
  return { open: requests.filter((r) => !added.includes(r)), added };
}
